<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'

import AppIcon from '../components/AppIcon.vue'
import SiteFooter from '../components/SiteFooter.vue'
import SiteHeader from '../components/SiteHeader.vue'
import { ApiError, apiRequest } from '../services/api'

const props = defineProps<{ code?: string }>()
const router = useRouter()
const gameCode = ref(props.code ?? '')
const displayName = ref('')
const error = ref('')
const busy = ref(false)

const steps = [
  { icon: 'qrcode', title: 'Join', text: 'Scan the code on the big screen or type the six-character Game ID. No app, no account.' },
  { icon: 'message', title: 'Answer', text: 'Play solo or form a team. Each question asks what the room will say, and every team locks one answer.' },
  { icon: 'trophy', title: 'Reveal', text: 'The host reveals the crowd’s favourites. Match the most popular answers to climb the standings.' },
]

async function join(): Promise<void> {
  error.value = ''
  busy.value = true
  try {
    const result = await apiRequest<{ credential: string; sessionId: string }>('/api/player/join', {
      method: 'POST', body: JSON.stringify({ joinCode: gameCode.value, displayName: displayName.value }),
    })
    localStorage.setItem(`crowd-control-player:${result.sessionId}`, result.credential)
    await router.push(`/play/${result.sessionId}`)
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'Unable to join the game.'
  } finally { busy.value = false }
}
</script>

<template>
  <div class="page-wrapper">
    <SiteHeader>
      <template #cta>
        <RouterLink
          to="/admin"
          class="btn--secondary"
        >
          <span class="btn-label">Host sign in</span><AppIcon name="arrow-right" />
        </RouterLink>
      </template>
      <template #mobile-cta>
        <RouterLink
          to="/admin"
          class="btn--secondary"
        >
          Host sign in <AppIcon name="arrow-right" />
        </RouterLink>
      </template>
    </SiteHeader>
    <main>
      <section
        class="hero"
        :style="{ backgroundImage: 'url(/images/hero-bg.png)' }"
      >
        <div class="container">
          <div class="grid grid--split">
            <div
              v-reveal
              class="hero__content"
            >
              <span class="sub-title text-xl"><strong>Live,</strong> crowd-powered survey game</span>
              <h1
                id="join-title"
                class="fw-7 mt-24"
              >
                Crowd Control
              </h1>
              <p class="text-xl mt-24">
                The room decides. Teams answer on their phones, the host reveals what the crowd said, and the most popular answers win the points.
              </p>
              <div class="banner-action mt-40">
                <a
                  href="#join"
                  class="btn--primary"
                >Join the game <AppIcon name="arrow-right" /></a>
                <RouterLink
                  to="/admin"
                  class="btn--secondary"
                >
                  Host a game <AppIcon name="arrow-right" />
                </RouterLink>
              </div>
              <div class="hero__review mt-60">
                <p class="text-lg">
                  Built for
                </p>
                <div
                  class="review"
                  aria-hidden="true"
                >
                  <AppIcon
                    v-for="index in 5"
                    :key="index"
                    name="star"
                    filled
                    :size="20"
                  />
                </div>
                <p class="text-lg">
                  pubs, parties, offices and classrooms.
                </p>
              </div>
            </div>
            <section
              id="join"
              v-reveal
              class="panel panel--pad join-card"
            >
              <span class="sub-title"><strong>Join</strong> in seconds</span>
              <h4
                id="join-heading"
                class="fw-6 mt-12"
              >
                Enter your Game ID
              </h4>
              <form
                class="stack stack--lg mt-30"
                @submit.prevent="join"
              >
                <p
                  v-if="error"
                  class="alert"
                  role="alert"
                >
                  {{ error }}
                </p>
                <div class="input-wrapper">
                  <label for="game-code">Game ID</label>
                  <div class="input-single code-input">
                    <AppIcon name="hash" />
                    <input
                      id="game-code"
                      v-model="gameCode"
                      autocomplete="off"
                      autocapitalize="characters"
                      maxlength="6"
                      placeholder="PK7M4X"
                      required
                    >
                  </div>
                </div>
                <div class="input-wrapper">
                  <label for="display-name">Your name</label>
                  <div class="input-single">
                    <AppIcon name="user" />
                    <input
                      id="display-name"
                      v-model="displayName"
                      autocomplete="nickname"
                      maxlength="40"
                      placeholder="Display name"
                      required
                    >
                  </div>
                </div>
                <button
                  type="submit"
                  class="btn--primary btn--block"
                  :disabled="busy"
                >
                  {{ busy ? 'Joining…' : 'Join game' }} <AppIcon name="arrow-right" />
                </button>
              </form>
            </section>
          </div>
        </div>
        <div class="star-img">
          <img
            src="/images/stars.png"
            alt=""
          >
        </div>
        <div class="trophy-img">
          <img
            src="/images/trophy.png"
            alt=""
          >
        </div>
        <div class="glow glow--one" />
        <div class="glow glow--two" />
      </section>

      <section class="section">
        <div class="container">
          <div
            v-reveal
            class="section__header text-center mb-55"
          >
            <span class="sub-title text-xl"><strong>How,</strong> it works</span>
            <h2 class="fw-6 mt-16">
              Three steps to <span>chaos</span>
            </h2>
            <p class="mt-16">
              No downloads and nothing to learn. Everyone plays from the phone already in their pocket.
            </p>
          </div>
          <div class="grid grid--3">
            <article
              v-for="(step, index) in steps"
              :key="step.title"
              v-reveal
              class="counter__single"
            >
              <div class="counter__single-thumb">
                <AppIcon :name="step.icon" />
              </div>
              <div class="mt-24">
                <h3 class="fw-6">
                  {{ index + 1 }}. {{ step.title }}
                </h3>
                <p class="text-lg mt-12">
                  {{ step.text }}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>
    </main>
    <SiteFooter />
  </div>
</template>
